import { BaseStreamRegistry } from './base'
import { DEFAULT_RETRY_CONFIG } from '../config'
import { ENDPOINTS } from '@shared/utils/constant'
import { StreamKey, type StreamHandlers } from '../types'
import { StreamManagerV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcTradingStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcDerivativesStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import { IndexerGrpcAccountPortfolioStreamV2 } from '@injectivelabs/sdk-ts/client/indexer'
import type {
  GridStrategyStreamCallbackV2,
  AccountPortfolioStreamCallbackV2,
  DerivativePositionsV2StreamCallbackV2
} from '@injectivelabs/sdk-ts'

const derivativesMarketStreamV2 = new IndexerGrpcDerivativesStreamV2(
  ENDPOINTS.indexer
)
const portfolioStreamV2 = new IndexerGrpcAccountPortfolioStreamV2(
  ENDPOINTS.indexer
)
const tradingStreamV2 = new IndexerGrpcTradingStreamV2(ENDPOINTS.indexer)

export class AccountStreamRegistry extends BaseStreamRegistry {
  subscribeSubaccountBalance(
    accountAddress: string,
    subaccountId: string,
    type?: string,
    handlers?: StreamHandlers<Parameters<AccountPortfolioStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.SubaccountBalance

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return portfolioStreamV2.streamAccountPortfolio({
          accountAddress,
          subaccountId,
          ...(type && { type }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers?.onData || (() => {}),
      retryConfig: DEFAULT_RETRY_CONFIG
    })

    this.registerStream(streamId, manager, handlers)
  }

  subscribeGridStrategies(
    accountAddresses: string[],
    marketId: string | undefined,
    handlers: StreamHandlers<Parameters<GridStrategyStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.GridStrategies

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return tradingStreamV2.streamGridStrategies({
          accountAddresses,
          ...(marketId && { marketId }),
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })
    this.registerStream(streamId, manager, handlers)
  }

  subscribePositions(
    address: string,
    handlers: StreamHandlers<
      Parameters<DerivativePositionsV2StreamCallbackV2>[0]
    >
  ): void {
    const streamId = StreamKey.Positions

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return derivativesMarketStreamV2.streamPositionsV2({
          address,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })

    this.registerStream(streamId, manager, handlers)
  }

  subscribeBankBalance(
    accountAddress: string,
    handlers: StreamHandlers<Parameters<AccountPortfolioStreamCallbackV2>[0]>
  ): void {
    const streamId = StreamKey.BankBalance

    const manager = new StreamManagerV2({
      id: streamId,
      streamFactory: () => {
        return portfolioStreamV2.streamAccountPortfolio({
          accountAddress,
          callback: (response) => manager.emit('data', response)
        })
      },
      onData: handlers.onData,
      retryConfig: DEFAULT_RETRY_CONFIG
    })

    this.registerStream(streamId, manager, handlers)
  }

  unsubscribeSubaccountBalance(): void {
    this.unsubscribe(StreamKey.SubaccountBalance)
  }

  unsubscribeGridStrategies(): void {
    this.unsubscribe(StreamKey.GridStrategies)
  }

  unsubscribeBankBalance(): void {
    this.unsubscribe(StreamKey.BankBalance)
  }

  unsubscribePositions(): void {
    this.unsubscribe(StreamKey.Positions)
  }
}
